import { useEffect, useState } from 'react';
import { Link2, Link2Off, RefreshCw, AlertCircle, CheckCircle2, Clock, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface SquareOAuthStatus {
  connected: boolean;
  merchant_id: string | null;
  connected_at: string | null;
  expires_at: string | null;
  expires_soon: boolean;
}

interface IntegrationSettingsProps {
  onNavigate: (page: string) => void;
  squareConnected?: boolean;
  squareError?: string;
}

export function IntegrationSettings({ onNavigate, squareConnected, squareError }: IntegrationSettingsProps) {
  const { user } = useAuth();
  const [squareStatus, setSquareStatus] = useState<SquareOAuthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const orgId = user?.organization?.id;
  const canManageTokens = user?.permissions?.can_manage_tokens ?? false;

  useEffect(() => {
    if (squareConnected) {
      setStatusMessage({ type: 'success', text: 'Square connected successfully!' });
    } else if (squareError) {
      const errorMessages: Record<string, string> = {
        invalid_state: 'The connection request expired. Please try again.',
        org_not_found: 'Organization not found. Please contact support.',
        access_denied: 'Square access was denied. Please try again and grant permissions.',
      };
      setStatusMessage({
        type: 'error',
        text: errorMessages[squareError] || `Square connection failed: ${squareError}`,
      });
    }
  }, [squareConnected, squareError]);

  useEffect(() => {
    if (orgId) {
      fetchSquareStatus();
    }
  }, [orgId]);

  const fetchSquareStatus = async () => {
    if (!orgId) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/square/oauth/status?organization_id=${orgId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSquareStatus(data);
      } else {
        setSquareStatus({ connected: false, merchant_id: null, connected_at: null, expires_at: null, expires_soon: false });
      }
    } catch (error) {
      console.error('Failed to fetch Square status:', error);
      setSquareStatus({ connected: false, merchant_id: null, connected_at: null, expires_at: null, expires_soon: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = () => {
    if (!orgId) return;

    const token = localStorage.getItem('auth_token');
    const returnUrl = `${window.location.origin}/settings`;
    const params = new URLSearchParams({
      organization_id: orgId,
      return_url: returnUrl,
    });
    if (token) {
      params.set('token', token);
    }

    window.location.href = `/api/square/oauth/authorize?${params.toString()}`;
  };

  const handleDisconnect = async () => {
    if (!orgId) return;

    setIsDisconnecting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/square/oauth/disconnect?organization_id=${orgId}`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.ok) {
        setSquareStatus({ connected: false, merchant_id: null, connected_at: null, expires_at: null, expires_soon: false });
        setStatusMessage({ type: 'success', text: 'Square disconnected successfully.' });
      } else {
        const data = await response.json();
        setStatusMessage({ type: 'error', text: data.detail || 'Failed to disconnect Square.' });
      }
    } catch (error) {
      console.error('Failed to disconnect Square:', error);
      setStatusMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-gray-600" />
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      </div>

      {statusMessage && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg flex items-center gap-2 ${
            statusMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span>{statusMessage.text}</span>
          <button
            onClick={() => setStatusMessage(null)}
            className="ml-auto text-current opacity-60 hover:opacity-100"
          >
            &times;
          </button>
        </div>
      )}

      <section>
        <h2 className="text-lg font-medium text-gray-800 mb-4">Integrations</h2>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${squareStatus?.connected ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {squareStatus?.connected ? (
                    <Link2 className="w-6 h-6 text-green-600" />
                  ) : (
                    <Link2Off className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-base font-medium text-gray-900">Square</h3>
                  <p className="text-sm text-gray-500">
                    {squareStatus?.connected
                      ? 'Connected — products sync to your Square catalog'
                      : 'Connect your Square account to sync products'}
                  </p>
                </div>
              </div>

              {!isLoading && squareStatus?.connected && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              )}
            </div>

            {isLoading && (
              <div className="mt-4 flex items-center gap-2 text-gray-400">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Checking connection status...</span>
              </div>
            )}

            {!isLoading && squareStatus?.connected && (
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                {squareStatus.merchant_id && (
                  <div>
                    <span className="text-gray-500">Merchant ID</span>
                    <p className="font-mono text-gray-700">{squareStatus.merchant_id}</p>
                  </div>
                )}
                {squareStatus.connected_at && (
                  <div>
                    <span className="text-gray-500">Connected</span>
                    <p className="text-gray-700">{formatDate(squareStatus.connected_at)}</p>
                  </div>
                )}
                {squareStatus.expires_at && (
                  <div className="flex items-center gap-1">
                    <div>
                      <span className="text-gray-500">Token Expires</span>
                      <p className={squareStatus.expires_soon ? 'text-amber-600 font-medium' : 'text-gray-700'}>
                        {formatDate(squareStatus.expires_at)}
                      </p>
                    </div>
                    {squareStatus.expires_soon && (
                      <Clock className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                )}
                {user?.organization?.square_environment && (
                  <div>
                    <span className="text-gray-500">Environment</span>
                    <p className="text-gray-700">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        user.organization.square_environment === 'production'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {user.organization.square_environment}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {!isLoading && squareStatus?.expires_soon && (
              <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                <Clock className="w-4 h-4 flex-shrink-0" />
                Token expires soon. It will auto-refresh, but if you experience issues, try reconnecting.
              </div>
            )}

            {!isLoading && canManageTokens && (
              <div className="mt-5 flex gap-3">
                {squareStatus?.connected ? (
                  <button
                    onClick={handleDisconnect}
                    disabled={isDisconnecting}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect Square'}
                  </button>
                ) : (
                  <button
                    onClick={handleConnect}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Connect Square
                  </button>
                )}
              </div>
            )}

            {!isLoading && !canManageTokens && (
              <p className="mt-4 text-sm text-gray-400">
                Contact your organization admin to manage Square integration.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
